import { UserService, UserServiceModel, UserServiceOptions } from '../origami-api/services/user.service'; //
import { SimpleTopic, TopicField, TopicUtils } from '../origami-api/utils/topic-utils';
import { FieldNames, FieldType, Locale } from '@app/origami-api/constants';
import { Observable, of } from 'rxjs';
import { catchError, map, take } from 'rxjs/operators';
import { ZoomDefault } from '@app/session/session.service';

export interface UserSetting<T> {
  data: T;
  matadata: {
    lastUpdated: number;
  };
}

export interface RoomStartPage {
  context: string;
  module: string;
}

export interface UserZoom {
  default: number;
}

export class UserPreferences {
  get startPage(): RoomStartPage {
    if (this._favoritePage === undefined) {
      const jsonText = TopicUtils.getTopicFieldValue(
        this.userDetails.UserTopic,
        'UserPreferences.UserDefaults.RoomStartPage',
      );
      this._favoritePage = this.getUserJsonFieldData<RoomStartPage>(jsonText, null);
      if (this._favoritePage && !this._favoritePage.context) {
        this._favoritePage = null;
      }
    }

    return this._favoritePage;
  }

  get locale(): Locale {
    return <Locale>TopicUtils.getTopicFieldValue(this.userDetails.GlobalUserTopic, FieldNames.User.Locale);
  }

  get userZoom(): UserZoom {
    return this.getUserJsonFieldData<UserZoom>(
      TopicUtils.getTopicFieldValue(this.userDetails.GlobalUserTopic, FieldNames.User.UserZoom),
      { default: ZoomDefault },
    );
  }

  private _favoritePage: RoomStartPage;

  constructor(
    private userDetails: UserServiceModel.User,
    private userService: UserService,
  ) {}

  getQuestionWindowAnswer(id: string): string {
    const data = this.getUserJsonFieldData<any>(
      TopicUtils.getTopicFieldValue(this.userDetails.UserTopic, FieldNames.User.QuestionsAnswers),
      { data: {} },
    );

    return data[id];
  }

  setQuestionWindowAnswer(roomId: number, id: string, value: string): void {
    const data = this.getUserJsonFieldData<any>(
      TopicUtils.getTopicFieldValue(this.userDetails.UserTopic, FieldNames.User.QuestionsAnswers),
      { data: {} },
    );

    data.data = data.data || {};

    data.data[id] = value;

    const sData = JSON.stringify(data);

    if (!this.userDetails.UserTopic) {
      this.userDetails.UserTopic = <SimpleTopic>{
        TopicFields: [],
      };
    }

    TopicUtils.setTopicFieldValue(
      this.userDetails.UserTopic,
      FieldNames.User.QuestionsAnswers,
      sData,
      FieldType.LongText,
    );

    this.save(roomId, false, {
      FieldName: FieldNames.User.QuestionsAnswers,
      FieldType: FieldType.LongText,
      Fieldvalue: sData,
    })
      .pipe(take(1))
      .subscribe();
  }

  getMuteMaintenanceBanner(): boolean {
    return !!TopicUtils.getTopicFieldValue(this.userDetails.GlobalUserTopic, FieldNames.User.MuteMaintenanceBanner);
  }

  getPreviousMaintenanceBannerHash(): boolean {
    return TopicUtils.getTopicFieldValue(
      this.userDetails.GlobalUserTopic,
      FieldNames.User.PreviousMaintenanceBannerHash,
    );
  }

  setMuteMaintenanceBanner(roomId: number, value: boolean, hash: string) {
    this.save(roomId, true, [
      {
        FieldName: FieldNames.User.MuteMaintenanceBanner,
        FieldType: FieldType.Number,
        Fieldvalue: value ? 1 : 0,
      },
      {
        FieldName: FieldNames.User.PreviousMaintenanceBannerHash,
        FieldType: FieldType.Text,
        Fieldvalue: hash,
      },
    ])
      .pipe(take(1))
      .subscribe();
  }

  save(roomId: number, global: boolean, topicField: TopicField | TopicField[]): Observable<boolean> {
    const user: UserServiceOptions.User = {
      UserId: this.userDetails.UserId,
      UserName: this.userDetails.UserName,
    };

    if (!Array.isArray(topicField)) {
      topicField = [topicField];
    }

    if (global) {
      user.GlobalUserTopic = { TopicFields: topicField };
    } else {
      user.UserTopic = { TopicFields: topicField };
    }

    return this.userService.editUser({ siteId: roomId, user: user }).pipe(
      map((res) => {
        if (res.Success) {
          const topic = global ? this.userDetails.GlobalUserTopic : this.userDetails.UserTopic;
          (topicField as TopicField[]).forEach((tf) => {
            TopicUtils.setTopicFieldValue(topic, tf.FieldName, tf.Fieldvalue, tf.FieldType);
          });
        }
        return res.Success;
      }),
      catchError(() => of(false)),
    );
  }

  private getUserJsonFieldData<T>(jsonText: string, emptyValue: T = null): T {
    try {
      const json = <UserSetting<any>>JSON.parse(jsonText);
      return json.data || emptyValue;
    } catch (e) {}

    return emptyValue;
  }
}
